import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import { AlertCircle, Bell, CheckCheck, Loader2, RefreshCw, X } from 'lucide-react'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import useNotificationStore, { selectTenantNotifications } from '../store/notificationStore'
import { useNotificationTenantId } from '../hooks/useNotificationTenantId'
import { useOpenNotification } from '../hooks/useBellNotifications'

// The branch's full notification history. All state lives in the shared
// notification store, which the bell dropdown reads too, so the page and the
// bell can never disagree; realtime updates reach it through
// NotificationSync. A notification's read state belongs to the whole branch.
// growchat-doc/manager-notification-page/spec-manager-notification-page.md

const TABS = [
  { filter: 'all', labelKey: 'notifications.tabAll' },
  { filter: 'unread', labelKey: 'notifications.tabUnread' },
  { filter: 'read', labelKey: 'notifications.tabRead' },
]

const EMPTY_STATE_KEYS = {
  all: ['notifications.emptyAllTitle', 'notifications.emptyAllDescription'],
  unread: ['notifications.emptyUnreadTitle', 'notifications.emptyUnreadDescription'],
  read: ['notifications.emptyReadTitle', 'notifications.emptyReadDescription'],
}

// The API sends `created_at` as UTC without a zone marker; `new Date()` would
// read that as local time and every "x minutes ago" would be off by the
// viewer's UTC offset.
function parseCreatedAt(value) {
  if (typeof value !== 'string') return null
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/.test(value)
  const date = new Date(hasZone ? value : `${value}Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

function formatWhen(value, t, locale) {
  const date = parseCreatedAt(value)
  if (!date) return ''
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000)
  if (minutes < 1) return t('notifications.justNow')
  if (minutes < 60) return t('notifications.minutesAgo', { count: minutes })
  if (minutes < 60 * 24) return t('notifications.hoursAgo', { count: Math.floor(minutes / 60) })
  return date.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function ListSkeleton() {
  return (
    <div className="space-y-4 p-4" aria-busy="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="animate-pulse space-y-2">
          <div className="h-3 w-2/5 rounded bg-slate-200" />
          <div className="h-3 w-4/5 rounded bg-slate-100" />
        </div>
      ))}
    </div>
  )
}

export default function NotificationsPage() {
  const { t, i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tab = tabParam === 'unread' || tabParam === 'read' ? tabParam : 'all'

  const notificationTenantId = useNotificationTenantId()

  const { items, unreadCount, status, hasMore, loadingMore, stale } = useNotificationStore(
    useShallow((s) => selectTenantNotifications(s, notificationTenantId, tab))
  )
  const storeReady = useNotificationStore((s) => Boolean(s.tenantId) && s.tenantId === notificationTenantId)
  const showTab = useNotificationStore((s) => s.showTab)
  const loadMore = useNotificationStore((s) => s.loadMore)
  const openNotification = useOpenNotification()
  const markAllRead = useNotificationStore((s) => s.markAllRead)
  const remove = useNotificationStore((s) => s.remove)

  // Loads the tab the first time it is shown, and again whenever it goes
  // stale while on screen (someone marked everything read).
  useEffect(() => {
    if (storeReady) showTab(tab)
  }, [storeReady, tab, stale, showTab])

  function selectTab(filter) {
    const params = new URLSearchParams(searchParams)
    if (filter === 'all') params.delete('tab')
    else params.set('tab', filter)
    setSearchParams(params, { replace: true })
  }

  const isLoadingFirstPage = !storeReady || status === 'idle' || status === 'loading'
  const [emptyTitleKey, emptyDescriptionKey] = EMPTY_STATE_KEYS[tab]

  let body
  if (status === 'error') {
    body = (
      <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
        <AlertCircle size={28} className="text-status-red" />
        <p className="text-sm text-slate-600">{t('notifications.loadError')}</p>
        <button
          type="button"
          onClick={() => showTab(tab)}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700"
        >
          <RefreshCw size={15} /> {t('notifications.retry')}
        </button>
      </div>
    )
  } else if (isLoadingFirstPage) {
    body = <ListSkeleton />
  } else if (items.length === 0) {
    body = (
      <div className="flex flex-col items-center gap-2 px-4 py-12 text-center">
        <Bell size={28} className="text-slate-300" />
        <p className="text-sm font-semibold text-slate-700">{t(emptyTitleKey)}</p>
        <p className="text-sm text-slate-400">{t(emptyDescriptionKey)}</p>
      </div>
    )
  } else {
    body = items.map((n) => (
      <div
        key={n.id}
        role="button"
        tabIndex={0}
        onClick={() => openNotification(n)}
        onKeyDown={(e) => {
          if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            openNotification(n)
          }
        }}
        className={`flex cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 hover:bg-slate-50 ${n.read ? '' : 'bg-primary-soft'}`}
      >
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? 'bg-transparent' : 'bg-primary'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className={`truncate text-sm ${n.read ? 'font-medium text-slate-600' : 'font-semibold text-slate-900'}`}>{n.title}</p>
            <span className="shrink-0 text-xs text-slate-400">{formatWhen(n.created_at, t, i18n.language)}</span>
          </div>
          <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">{n.body}</p>
        </div>
        <button
          type="button"
          aria-label={t('notifications.delete')}
          title={t('notifications.delete')}
          onClick={(e) => {
            e.stopPropagation()
            remove(n.id)
          }}
          className="-mr-2 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-300 hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={16} />
        </button>
      </div>
    ))
  }

  const showFooter = status === 'loaded' && storeReady && items.length > 0

  return (
    <ResponsiveShell title={t('notifications.title')}>
      <div className="mx-auto max-w-3xl pb-10 lg:px-8">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xl font-bold text-slate-950">
              <Bell size={22} className="text-primary" />
              <h1 className="truncate">{t('notifications.title')}</h1>
            </div>
            <p className="mt-1 text-sm text-slate-500">{t('notifications.unreadSummary', { count: unreadCount })}</p>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700"
            >
              <CheckCheck size={16} />
              <span className="hidden sm:inline">{t('notifications.markAllRead')}</span>
              <span className="sm:hidden">{t('notifications.markAllReadShort')}</span>
            </button>
          )}
        </div>

        <div className="mb-3 flex gap-1 border-b border-slate-200" role="tablist">
          {TABS.map(({ filter, labelKey }) => (
            <button
              key={filter}
              type="button"
              role="tab"
              aria-selected={tab === filter}
              onClick={() => selectTab(filter)}
              className={`px-3 py-2 text-sm font-medium ${tab === filter ? 'border-b-2 border-primary text-primary' : 'text-slate-500 hover:text-slate-800'}`}
            >
              {t(labelKey)}
              {filter === 'unread' && unreadCount > 0 ? ` (${unreadCount})` : ''}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">{body}</div>

        {showFooter && (
          <div className="mt-4 text-center">
            {hasMore ? (
              <button
                type="button"
                onClick={() => loadMore(tab)}
                disabled={loadingMore}
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 disabled:opacity-60"
              >
                {loadingMore && <Loader2 size={15} className="animate-spin" />}
                {t('notifications.loadMore')}
              </button>
            ) : (
              <p className="text-xs text-slate-400">{t('notifications.allShown', { count: items.length })}</p>
            )}
          </div>
        )}
      </div>
    </ResponsiveShell>
  )
}
